
import { defineComponent, reactive, onBeforeMount } from 'vue'

// components
import OkSelect from '@/modules/OktellModule/components/OkSelect/index.vue'
import OkInput from '@/modules/OktellModule/components/OkInput/index.vue'
import OkCheckbox from '@/modules/OktellModule/components/OkCheckbox/index.vue'
import OkButton from '@/modules/OktellModule/components/OkButton/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { cities } from '@/core/realisations/cities.realisation'
import { directions } from '@/core/realisations/directions.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// types
import { CityT } from '@/core/types/cities.types'
import { DirectionT } from '@/core/types/directions.types'
import { CompanyT } from '@/core/types/companies.types'
import {
    CalculatorComponentPayload,
    CalculatorPhysicalWeight,
    CalculatorVolumeWeight,
    CalculatorAdditionalService,
} from '@/core/types/billing.types'

// composable
import useNotifications from '@/common/composable/useNotifications'
import useHelpers from '@/common/composable/useHelpers'

// constants
const CALCULATOR_TARIFFS = [
    { id: 1, name: 'Стандартный тариф' },
    // { id: 2, name: 'Индивидуальный тариф', },
    // { id: 3, name: 'Уникальный тариф', },
]

// validation
import useVuelidate from '@vuelidate/core'
import { required, requiredIf, decimal } from '@vuelidate/validators'

export default defineComponent({
    components: {
        OkSelect,
        OkInput,
        OkCheckbox,
        OkButton,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const { findObjectByValue } = useHelpers()

        const state = reactive({
            is_loading: false,
            tariff_id: 1,
            company_id: null,
            from_city: null as CityT | null,
            from_city_id: null as number | null,
            where_city: null as CityT | null,
            where_city_id: null as number | null,
            declaredPrice: null as string | null,
            productName: null as string | null,
            volumeWeight: [
                {
                    width: null as string | null,
                    length: null as string | null,
                    height: null as string | null,
                },
            ],
            physicalWeights: [{ weight: null as string | null }],
            is_has_pickup: false,
            is_has_self_delivery: false,
            isOrderPrice: false,
            additional_services: {
                soft_package_value: null as string | null,
                pallet_value: null as string | null,
                grid_value: null as string | null,
                manipulator_value: null as string | null,
                manipulator_duration: null as string | null,
                manipulator_costPerHour: null as string | null,
                crane_value: null as string | null,
                crane_duration: null as string | null,
                crane_costPerHour: null as string | null,
                loader_value: null as string | null,
                loader_duration: null as string | null,
                hydraulic_trolley_value: null as string | null,
                hydraulic_trolley_duration: null as string | null,
            },
        })

        const state_form$ = useVuelidate(
            {
                tariff_id: { required },
                from_city: { required },
                where_city: { required },
                declaredPrice: { decimal },
                additional_services: {
                    soft_package_value: { decimal },
                    pallet_value: { decimal },
                    grid_value: { decimal },
                    //
                    manipulator_value: {
                        decimal,
                        required: requiredIf(() => {
                            if (
                                state.additional_services.manipulator_duration ||
                                state.additional_services.manipulator_costPerHour
                            ) {
                                return true
                            }

                            return false
                        }),
                    },
                    manipulator_duration: {
                        decimal,
                        required: requiredIf(() => {
                            if (
                                state.additional_services.manipulator_value ||
                                state.additional_services.manipulator_costPerHour
                            ) {
                                return true
                            }

                            return false
                        }),
                    },
                    manipulator_costPerHour: {
                        decimal,
                        required: requiredIf(() => {
                            if (
                                state.additional_services.manipulator_value ||
                                state.additional_services.manipulator_duration
                            ) {
                                return true
                            }

                            return false
                        }),
                    },
                    //
                    crane_value: {
                        decimal,
                        required: requiredIf(() => {
                            if (
                                state.additional_services.crane_duration ||
                                state.additional_services.crane_costPerHour
                            ) {
                                return true
                            }

                            return false
                        }),
                    },
                    crane_duration: {
                        decimal,
                        required: requiredIf(() => {
                            if (
                                state.additional_services.crane_value ||
                                state.additional_services.crane_costPerHour
                            ) {
                                return true
                            }

                            return false
                        }),
                    },
                    crane_costPerHour: {
                        decimal,
                        required: requiredIf(() => {
                            if (
                                state.additional_services.crane_value ||
                                state.additional_services.crane_duration
                            ) {
                                return true
                            }

                            return false
                        }),
                    },
                    //
                    loader_value: {
                        decimal,
                        required: requiredIf(() => {
                            if (state.additional_services.loader_duration) {
                                return true
                            }

                            return false
                        }),
                    },
                    loader_duration: {
                        decimal,
                        required: requiredIf(() => {
                            if (state.additional_services.loader_value) {
                                return true
                            }

                            return false
                        }),
                    },
                    //
                    hydraulic_trolley_value: {
                        decimal,
                        required: requiredIf(() => {
                            if (state.additional_services.hydraulic_trolley_duration) {
                                return true
                            }

                            return false
                        }),
                    },
                    hydraulic_trolley_duration: {
                        decimal,
                        required: requiredIf(() => {
                            if (state.additional_services.hydraulic_trolley_value) {
                                return true
                            }

                            return false
                        }),
                    },
                },
            },
            state
        )

        const data_state = reactive({
            from_cities: new DataState<CityT>({}),
            where_cities: new DataState<CityT>({}),
            companies: new DataState<CompanyT>({}),
        })

        const direction_state = reactive({
            is_loading: false,
            is_failed: false,
            is_loaded: false,
            direction: null as DirectionT | null,
        })

        const loadFromCities = async () => {
            data_state.from_cities.pagination.current_page = 1

            try {
                data_state.from_cities.is_loading = true

                const response = await cities.getAllList(1)
                data_state.from_cities.list = response.data
                data_state.from_cities.pagination.total_pages = response.meta.last_page

                data_state.from_cities.is_failed = false
                data_state.from_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.from_cities.failure.catchError(error)

                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_failed = true
                }
            } finally {
                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_loading = false
                }
            }
        }

        const loadWhereCities = async (sender_city_id: number) => {
            data_state.where_cities.pagination.current_page = 1

            try {
                data_state.where_cities.is_loading = true

                const response = await cities.getCitiesBySender(sender_city_id)
                data_state.where_cities.list = response.data

                data_state.where_cities.is_failed = false
                data_state.where_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.where_cities.failure.catchError(error)

                if (!data_state.where_cities.failure.error_state.is_cancel) {
                    data_state.where_cities.is_failed = true
                }
            } finally {
                if (!data_state.where_cities.failure.error_state.is_cancel) {
                    data_state.where_cities.is_loading = false
                }
            }
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    shipmentTariffId: state.tariff_id,
                    limit: 9000,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const onTypeChange = (type_id: number) => {
            if (type_id !== 1) {
                loadCompanies(1)
            }

            state.company_id = null
        }

        const onFromCityChange = (from_city_id: number | null) => {
            if (!from_city_id) {
                state.from_city = null

                data_state.where_cities.list = []
                data_state.where_cities.is_loading = false
                data_state.where_cities.is_failed = false
                data_state.where_cities.failure.error_state.is_cancel = false
                data_state.where_cities.pagination.current_page = 1
                data_state.where_cities.pagination.total_pages = 1

                return
            }

            const city = findObjectByValue('id', from_city_id, data_state.from_cities.list)
            state.from_city = { ...(city as CityT) }

            loadWhereCities(from_city_id)
        }

        const onWhereCityChange = (where_city_id: number | null) => {
            if (!where_city_id) {
                state.where_city = null
                return
            }

            const city = findObjectByValue('id', where_city_id, data_state.where_cities.list)
            state.where_city = { ...(city as CityT) }
        }

        const removePlace = (place_index: number) => {
            state.volumeWeight.splice(place_index, 1)
            state.physicalWeights.splice(place_index, 1)
        }

        const addPlace = () => {
            state.volumeWeight.push({ width: null, length: null, height: null })
            state.physicalWeights.push({ weight: null })
        }

        const onToggleIsHasPickup = () => {
            if (props.loading || state.is_loading) return
            state.is_has_pickup = !state.is_has_pickup
        }

        const onToggleIsHasSelfDelivery = () => {
            if (props.loading || state.is_loading) return
            state.is_has_self_delivery = !state.is_has_self_delivery
        }

        const onToggleIsOrderPrice = () => {
            if (props.loading || state.is_loading) return
            state.isOrderPrice = !state.isOrderPrice
        }

        const resetDirection = () => {
            direction_state.is_failed = false
            direction_state.is_loaded = false
            direction_state.direction = null
        }

        const loadDirection = async () => {
            resetDirection()

            state_form$.value.from_city.$touch()
            state_form$.value.where_city.$touch()

            if (state_form$.value.from_city.$invalid || state_form$.value.where_city.$invalid)
                return

            try {
                direction_state.is_loading = true

                const fromCityId = state.from_city_id as number
                const whereCityId = state.where_city_id as number

                const params = {
                    page: 1,
                    fromCityId,
                    whereCityId,
                    //
                    companyId: state.company_id,
                    isExtended: 1,
                    hasSelfDelivery: state.is_has_self_delivery ? 1 : 0,
                    hasPickup: state.is_has_pickup ? 1 : 0,
                }

                const response = await directions.getList(params)

                direction_state.is_failed = false
                direction_state.is_loaded = true

                if (response.data.length > 0) {
                    direction_state.direction = response.data[0]
                }
            } catch (error) {
                console.error(error)

                direction_state.is_failed = true
                direction_state.is_loaded = false
                notifications.notifyError({
                    error,
                    title: 'При получении направления произошла ошибка',
                })
            } finally {
                direction_state.is_loading = false
            }
        }

        const placeValidationNotification = (place_number: number, message: string) => {
            notifications.notify({ title: `Место #${place_number}: ${message}`, type: 'danger' })
        }

        const validatePlaces = (): boolean => {
            let is_invalid = false

            state.volumeWeight.forEach((volume_weight, index) => {
                if (!state.physicalWeights[index].weight) {
                    is_invalid = true
                    placeValidationNotification(index + 1, 'Не указан вес')
                }

                if (!volume_weight.length) {
                    is_invalid = true
                    placeValidationNotification(index + 1, 'Не указана длина')
                }

                if (!volume_weight.width) {
                    is_invalid = true
                    placeValidationNotification(index + 1, 'Не указана ширина')
                }

                if (!volume_weight.height) {
                    is_invalid = true
                    placeValidationNotification(index + 1, 'Не указана высота')
                }
            })

            return is_invalid
        }

        const replaceComma = (stringed_number: string | null) => {
            if (!stringed_number) return null
            return stringed_number.replace(',', '.')
        }

        const mapPhysicalWeights = (physical_weight: {
            weight: string | null
        }): CalculatorPhysicalWeight => {
            const weight = replaceComma(physical_weight.weight)

            return {
                weight: Number(weight),
            }
        }

        const mapVolumeWeights = (volume_weight: {
            width: string | null
            length: string | null
            height: string | null
        }): CalculatorVolumeWeight => {
            const width = replaceComma(volume_weight.width)
            const length = replaceComma(volume_weight.length)
            const height = replaceComma(volume_weight.height)

            return {
                width: Number(width),
                length: Number(length),
                height: Number(height),
            }
        }

        const getAdditionalServices = (): CalculatorAdditionalService[] => {
            const calculator_services: CalculatorAdditionalService[] = []

            if (state.additional_services.soft_package_value) {
                calculator_services.push({
                    code: 'soft_package',
                    value: Number(state.additional_services.soft_package_value),
                    duration: 0,
                    costPerHour: 0,
                    costTotal: 0,
                    paidPricePerHour: 0,
                    paidPriceTotal: 0,
                })
            }

            if (state.additional_services.pallet_value) {
                calculator_services.push({
                    code: 'pallet',
                    value: Number(state.additional_services.pallet_value),
                    duration: 0,
                    costPerHour: 0,
                    costTotal: 0,
                    paidPricePerHour: 0,
                    paidPriceTotal: 0,
                })
            }

            if (state.additional_services.grid_value) {
                calculator_services.push({
                    code: 'grid',
                    value: Number(state.additional_services.grid_value),
                    duration: 0,
                    costPerHour: 0,
                    costTotal: 0,
                    paidPricePerHour: 0,
                    paidPriceTotal: 0,
                })
            }

            if (state.additional_services.manipulator_value) {
                calculator_services.push({
                    code: 'manipulator',
                    value: Number(state.additional_services.manipulator_value),
                    duration: Number(state.additional_services.manipulator_duration),
                    costPerHour: Number(state.additional_services.manipulator_costPerHour),
                    costTotal: 0,
                    paidPricePerHour: 0,
                    paidPriceTotal: 0,
                })
            }

            if (state.additional_services.crane_value) {
                calculator_services.push({
                    code: 'crane',
                    value: Number(state.additional_services.crane_value),
                    duration: Number(state.additional_services.crane_duration),
                    costPerHour: Number(state.additional_services.crane_costPerHour),
                    costTotal: 0,
                    paidPricePerHour: 0,
                    paidPriceTotal: 0,
                })
            }

            if (state.additional_services.loader_value) {
                calculator_services.push({
                    code: 'loader',
                    value: Number(state.additional_services.loader_value),
                    duration: Number(state.additional_services.loader_duration),
                    costPerHour: 0,
                    costTotal: 0,
                    paidPricePerHour: 0,
                    paidPriceTotal: 0,
                })
            }

            if (state.additional_services.hydraulic_trolley_value) {
                calculator_services.push({
                    code: 'hydraulic_trolley',
                    value: Number(state.additional_services.hydraulic_trolley_value),
                    duration: Number(state.additional_services.hydraulic_trolley_duration),
                    costPerHour: 0,
                    costTotal: 0,
                    paidPricePerHour: 0,
                    paidPriceTotal: 0,
                })
            }

            return calculator_services
        }

        const onSubmitForm = async () => {
            if (props.loading || direction_state.is_loading) return

            await loadDirection()

            state_form$.value.$touch()

            if (!direction_state.direction) {
                notifications.notify({ title: 'Направление не загружено', type: 'danger' })
            }

            if (state_form$.value.$invalid || !direction_state.direction) return
            if (validatePlaces()) return

            const payload: CalculatorComponentPayload = {
                tariff_id: state.tariff_id,
                company_id: state.company_id,
                from_city: { ...(state.from_city as CityT) },
                where_city: { ...(state.where_city as CityT) },
                direction: { ...direction_state.direction },
                is_has_pickup: state.is_has_pickup,
                is_has_self_delviery: state.is_has_self_delivery,
                isOrderPrice: state.isOrderPrice,
                physicalWeights: state.physicalWeights.map(mapPhysicalWeights),
                volumeWeights: state.volumeWeight.map(mapVolumeWeights),
                additionalServicesDelivery: getAdditionalServices(),
            }

            if (Number(state.declaredPrice)) {
                payload.declaredPrice = Number(state.declaredPrice)
            }

            if (state.productName) {
                payload.productName = state.productName
            }

            emit('submit', payload)
        }

        onBeforeMount(() => {
            loadFromCities()
        })

        return {
            CALCULATOR_TARIFFS,
            state,
            state_form$,
            data_state,
            direction_state,
            loadFromCities,
            loadWhereCities,
            loadCompanies,
            onTypeChange,
            onFromCityChange,
            onWhereCityChange,
            removePlace,
            addPlace,
            onToggleIsHasPickup,
            onToggleIsHasSelfDelivery,
            onToggleIsOrderPrice,
            onSubmitForm,
        }
    },
})
